const LIST_SELECTOR = '[data-product-list]';
const CARD_SELECTOR = 'li[data-catalog-card]';
const PROMOTION_CARD_SELECTOR = '[data-promo-card-position]';
const CAROUSEL_SELECTOR = 'grid-carousel';
const SORT_CONFIG_SELECTOR = '[data-storefront-sort-config]';
const STOREFRONT_PRODUCT_SELECTOR = '[data-storefront-product-card]';

const pendingLists = new WeakSet();
const pendingCarousels = new WeakSet();

function variantId(card) {
  const parsed = Number.parseInt(card.dataset.catalogVariantId || '0', 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeVariantGroups(list) {
  list.querySelectorAll('.product-grid__variant-group').forEach((group) => {
    const remainingCards = [...group.querySelectorAll(CARD_SELECTOR)]
      .filter((card) => card.closest(LIST_SELECTOR) === list);

    if (remainingCards.length === 0) {
      group.remove();
    } else if (remainingCards.length === 1) {
      group.replaceWith(remainingCards[0]);
    }
  });
}

function topLevelListItem(card, list) {
  let item = card;

  while (item.parentElement && item.parentElement !== list) {
    item = item.parentElement;
  }

  return item.parentElement === list ? item : null;
}

export function stablePriorityOrder(items, priorityItems, fixedItems) {
  return stableCatalogOrder(items, new Map(), priorityItems, fixedItems);
}

export function stableCatalogOrder(items, sortOrders, priorityItems, fixedItems) {
  const orderedItems = [];
  let segment = [];

  const flushSegment = () => {
    orderedItems.push(
      ...segment
        .map((item, index) => ({
          item,
          index,
          priority: priorityItems.has(item),
          sortOrder: sortOrders.get(item),
        }))
        .sort((a, b) => {
          const aHasSortOrder = Number.isFinite(a.sortOrder);
          const bHasSortOrder = Number.isFinite(b.sortOrder);

          if (aHasSortOrder || bHasSortOrder) {
            if (aHasSortOrder && bHasSortOrder) {
              return a.sortOrder - b.sortOrder || a.index - b.index;
            }

            return aHasSortOrder ? -1 : 1;
          }

          return Number(b.priority) - Number(a.priority) || a.index - b.index;
        })
        .map(({ item }) => item),
    );
    segment = [];
  };

  items.forEach((item) => {
    if (fixedItems.has(item)) {
      flushSegment();
      orderedItems.push(item);
      return;
    }
    segment.push(item);
  });
  flushSegment();

  return orderedItems;
}

function storefrontSortOrder(item) {
  const source = item.matches('[data-storefront-sort-order]')
    ? item
    : item.querySelector('[data-storefront-sort-order]');
  const parsed = Number.parseInt(source?.dataset.storefrontSortOrder || '', 10);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function storefrontSortOrders(items) {
  const sortOrders = new Map();

  items.forEach((item) => {
    const order = storefrontSortOrder(item);
    if (order !== null) sortOrders.set(item, order);
  });

  return sortOrders;
}

function prioritizeCards(list) {
  const children = [...list.children];
  const sortMode = list.dataset.catalogSortMode || 'legacy';
  if (sortMode === 'shopify') return;

  const priorityItems = new Set(
    [...list.querySelectorAll(`${CARD_SELECTOR}[data-catalog-priority="true"]`)]
      .filter((card) => card.closest(LIST_SELECTOR) === list)
      .map((card) => topLevelListItem(card, list))
      .filter(Boolean),
  );
  const sortOrders = sortMode === 'standard' ? storefrontSortOrders(children) : new Map();

  if (priorityItems.size === 0 && sortOrders.size === 0) return;

  const fixedItems = new Set(children.filter((item) => item.matches(PROMOTION_CARD_SELECTOR)));
  const orderedChildren = stableCatalogOrder(children, sortOrders, priorityItems, fixedItems);

  if (orderedChildren.every((item, index) => item === children[index])) return;
  list.append(...orderedChildren);
}

function sortCarousel(carousel) {
  pendingCarousels.delete(carousel);

  const config = [...carousel.children].find((item) => item.matches(SORT_CONFIG_SELECTOR));
  if (!config || config.dataset.enabled !== 'true') return;

  const children = [...carousel.children];
  const productItems = children.filter((item) => item.matches(STOREFRONT_PRODUCT_SELECTOR));
  const sortOrders = storefrontSortOrders(productItems);
  const fixedItems = new Set(
    children.filter((item) => item === config || item.matches(PROMOTION_CARD_SELECTOR)),
  );
  const orderedChildren = stableCatalogOrder(children, sortOrders, new Set(), fixedItems);

  if (!orderedChildren.every((item, index) => item === children[index])) {
    carousel.append(...orderedChildren);
  }

  const limit = Number.parseInt(config.dataset.limit || '', 10);
  if (!Number.isFinite(limit) || limit < 1) return;

  [...carousel.children]
    .filter((item) => item.matches(STOREFRONT_PRODUCT_SELECTOR))
    .slice(limit)
    .forEach((item) => item.remove());
}

function dedupeList(list) {
  pendingLists.delete(list);

  const cards = [...list.querySelectorAll(CARD_SELECTOR)]
    .filter((card) => card.closest(LIST_SELECTOR) === list)
    .map((card, index) => ({ card, index }))
    .sort((a, b) => variantId(b.card) - variantId(a.card) || a.index - b.index);
  const seenEans = new Set();
  const seenScsPacks = new Set();

  cards.forEach(({ card }) => {
    const ean = (card.dataset.catalogEan || '').trim();
    const scsPack = (card.dataset.catalogScsPack || '').trim();
    const duplicate = (ean && seenEans.has(ean)) || (scsPack && seenScsPacks.has(scsPack));

    if (duplicate) {
      card.remove();
      return;
    }

    if (ean) seenEans.add(ean);
    if (scsPack) seenScsPacks.add(scsPack);
  });

  normalizeVariantGroups(list);
  prioritizeCards(list);
}

function scheduleList(list) {
  if (!(list instanceof Element) || pendingLists.has(list)) return;
  pendingLists.add(list);
  queueMicrotask(() => dedupeList(list));
}

function scheduleCarousel(carousel) {
  if (!(carousel instanceof Element) || pendingCarousels.has(carousel)) return;
  pendingCarousels.add(carousel);
  queueMicrotask(() => sortCarousel(carousel));
}

function scan(root = document) {
  if (!(root instanceof Document || root instanceof Element)) return;
  if (root instanceof Element && root.matches(LIST_SELECTOR)) scheduleList(root);
  root.querySelectorAll(LIST_SELECTOR).forEach(scheduleList);

  if (root instanceof Element && root.matches(SORT_CONFIG_SELECTOR)) {
    scheduleCarousel(root.closest(CAROUSEL_SELECTOR));
  }
  root.querySelectorAll(SORT_CONFIG_SELECTOR).forEach((config) => {
    scheduleCarousel(config.closest(CAROUSEL_SELECTOR));
  });
}

if (typeof document !== 'undefined') {
  scan();

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (!(mutation.target instanceof Element)) return;
      const list = mutation.target.closest(LIST_SELECTOR);
      if (list) scheduleList(list);
      const carousel = mutation.target.closest(CAROUSEL_SELECTOR);
      if (carousel?.querySelector(SORT_CONFIG_SELECTOR)) scheduleCarousel(carousel);
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  ['section:content-updated', 'section:updated'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => scan(event.target));
  });
}
